import axios from "axios";

const urlPath = "asistente";

// El back puede tardar (el modelo encadena varias consultas): margen por encima de ASISTENTE_TIMEOUT_MS
const TIMEOUT_MS = 120000;

// detalle: datos extra del back (p. ej. LIMITE_DIARIO -> { limite, restantes, reinicio })
const crearError = (codigo, status, detalle) => {
  const err = new Error(codigo);
  err.codigo = codigo;
  err.status = status;
  if (detalle) err.detalle = detalle;
  return err;
};

/**
 * Llamada con axios que devuelve `data` o lanza un Error con `codigo` (código del back o ERROR_RED).
 * El interceptor resuelve los 401/403/500 y rechaza el resto con error.response
 * (o con el error de axios si no hubo respuesta, p. ej. timeout).
 */
const llamar = async (metodo, ruta, datos, opciones = {}) => {
  let response;
  let codigoRed = "ERROR_RED";
  try {
    response = await axios({ method: metodo, url: `${urlPath}/${ruta}`, data: datos, timeout: 30000, ...opciones });
  } catch (error) {
    response = error?.status ? error : error?.response;
    if (error?.code === "ECONNABORTED") codigoRed = "ASISTENTE_TIMEOUT";
  }
  if (response?.status >= 200 && response?.status < 300) return response.data;
  throw crearError(response?.data?.error || codigoRed, response?.status, response?.data?.detalle);
};

/**
 * Pregunta esperando la respuesta completa (POST /api/asistente/preguntar). La pantalla usa el streaming.
 * Devuelve { conversacion_id, respuesta, completa, ... }.
 */
export const preguntarAsistente = (pregunta, { conversacionId, audio } = {}) =>
  llamar("post", "preguntar", { pregunta, conversacion_id: conversacionId, audio }, { timeout: TIMEOUT_MS });

// Historial de conversaciones ---------------------------------------------------------------------

/** [{ id, titulo, ultimo_mensaje_at, creada_at }] (las más recientes primero) */
export const listarConversaciones = (buscar) =>
  llamar("get", "conversaciones", undefined, { params: buscar ? { buscar } : undefined });

/** { id, titulo, ..., mensajes: [{ id, rol, texto, estado, audio, error?, creado_at }] } */
export const obtenerConversacion = (id) => llamar("get", `conversaciones/${encodeURIComponent(id)}`);

export const renombrarConversacion = (id, titulo) => llamar("patch", `conversaciones/${encodeURIComponent(id)}`, { titulo });

/** Consultas a la IA que quedan hoy: { limite, usadas, restantes, agotado, reinicio } (limite null = sin límite) */
export const obtenerLimiteAsistente = () => llamar("get", "limite");

export const eliminarConversacion = (id) => llamar("delete", `conversaciones/${encodeURIComponent(id)}`);

/** Apunta el ticket del Chat de LegalIA creado desde una respuesta derivada a soporte. */
export const marcarTicketDerivacion = (conversacionId, mensajeId, ticketId) =>
  llamar("patch", `conversaciones/${encodeURIComponent(conversacionId)}/mensajes/${encodeURIComponent(mensajeId)}/derivacion`, { ticket_id: ticketId });

/** Pulgar arriba (util: true) o abajo (util: false) sobre una respuesta. Devuelve { mensaje_id, feedback }. */
export const enviarFeedback = (conversacionId, mensajeId, util) =>
  llamar("patch", `conversaciones/${encodeURIComponent(conversacionId)}/mensajes/${encodeURIComponent(mensajeId)}/feedback`, { util });

/**
 * Transcribe una pregunta por voz (POST /api/asistente/transcribir, multipart "audio" en WAV).
 * Devuelve { texto, segundos } o lanza un Error con `codigo`. El audio no se guarda en el servidor.
 */
export const transcribirAudio = (wav) => {
  const formulario = new FormData();
  formulario.append("audio", wav, "pregunta.wav");
  return llamar("post", "transcribir", formulario, { timeout: 60000 });
};

// Mismas cabeceras que pone el interceptor de axios (interceptors/axios.interceptor.jsx)
const cabeceras = () => {
  const token = localStorage.getItem("token");
  return {
    "Content-Type": "application/json",
    "Accept-Language": localStorage.getItem("i18nextLng") ?? "es",
    provide: "laefincas",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const urlApi = (ruta) => {
  const base = axios.defaults.baseURL || "";
  return `${base.endsWith("/") ? base : `${base}/`}${ruta}`;
};

// Un bloque SSE ("event: x\ndata: {...}") -> { evento, datos }
const leerBloque = (bloque) => {
  let evento = "message";
  const datos = [];
  for (const linea of bloque.split(/\r?\n/)) {
    if (linea.startsWith(":")) continue; // comentario / latido
    if (linea.startsWith("event:")) evento = linea.slice(6).trim();
    else if (linea.startsWith("data:")) datos.push(linea.slice(5).trimStart());
  }
  if (!datos.length) return null;
  try {
    return { evento, datos: JSON.parse(datos.join("\n")) };
  } catch {
    return null;
  }
};

/**
 * Pregunta al asistente recibiendo la respuesta en streaming (POST /api/asistente/preguntar/stream, SSE).
 * - conversacionId: conversación donde seguir (si no, el back crea una y la avisa con onConversacion({ id, titulo, nueva })).
 * - audio: la pregunta viene de un audio transcrito.
 * - reemplazarUltima: sustituye el último turno de la conversación (editar la última pregunta o volver a generar).
 * - onTexto(trozo): cada trozo de la respuesta según llega.
 * - onDerivacion({ destino, consulta }): la pregunta es para otro canal (soporte -> Chat de LegalIA / contactar; juridico).
 * - onHerramienta(nombre): el asistente está consultando algo (buscar_comunidades, detalle_visita...).
 * - onTarjeta(tarjeta): documento o mapa que se enseña debajo de la respuesta (mostrar_documento / mostrar_mapa).
 * - signal: AbortSignal para detener la respuesta.
 * Devuelve el JSON final (igual que preguntarAsistente) o lanza un Error con `codigo`.
 */
export const preguntarAsistenteStream = async (pregunta, {
  conversacionId, audio, reemplazarUltima, onConversacion, onTexto, onHerramienta, onDerivacion, onTarjeta, signal,
} = {}) => {
  let response;
  try {
    response = await fetch(urlApi(`${urlPath}/preguntar/stream`), {
      method: "POST",
      headers: cabeceras(),
      body: JSON.stringify({
        pregunta, conversacion_id: conversacionId, audio: !!audio, ...(reemplazarUltima ? { reemplazar_ultima: true } : {}),
      }),
      signal,
    });
  } catch (error) {
    throw crearError(error?.name === "AbortError" ? "ASISTENTE_CANCELADO" : "ERROR_RED");
  }

  // Errores antes de empezar (validación, límite de peticiones...) llegan como JSON normal
  if (!response.ok || !(response.headers.get("content-type") || "").includes("text/event-stream")) {
    let datos = {};
    try {
      datos = await response.json();
    } catch {
      // sin cuerpo JSON
    }
    throw crearError(datos.error || "ERROR_RED", response.status, datos.detalle);
  }

  const lector = response.body.getReader();
  const decodificador = new TextDecoder();
  let buffer = "";
  let final = null;
  try {
    while (true) {
      const { value, done } = await lector.read();
      if (done) break;
      buffer += decodificador.decode(value, { stream: true });
      let corte;
      while ((corte = buffer.search(/\r?\n\r?\n/)) >= 0) {
        const bloque = leerBloque(buffer.slice(0, corte));
        buffer = buffer.slice(corte).replace(/^\r?\n\r?\n/, "");
        if (!bloque) continue;
        if (bloque.evento === "texto") onTexto?.(bloque.datos.texto || "");
        else if (bloque.evento === "herramienta") onHerramienta?.(bloque.datos.herramienta);
        else if (bloque.evento === "conversacion") onConversacion?.(bloque.datos.conversacion);
        else if (bloque.evento === "derivacion") onDerivacion?.(bloque.datos.derivacion);
        else if (bloque.evento === "tarjeta") onTarjeta?.(bloque.datos.tarjeta);
        else if (bloque.evento === "fin") final = bloque.datos;
        else if (bloque.evento === "error") throw crearError(bloque.datos.error || "ERROR_RED", undefined, bloque.datos.detalle);
      }
    }
  } catch (error) {
    if (error?.codigo) throw error;
    throw crearError(error?.name === "AbortError" ? "ASISTENTE_CANCELADO" : "ERROR_RED");
  }
  if (!final) throw crearError("ERROR_RED");
  return final;
};
